import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ReportRow {
  readonly reportId: string;
  readonly reportCode: string;
  readonly reportName: string;
  readonly reportType: string;
  readonly category: string | null;
  readonly outputFormat: string;
  readonly status: string;
  readonly lastRunStatus: string;
  readonly scheduleEnabled: boolean;
}

const TYPES = ['SCHEDULED', 'ON_DEMAND', 'AD_HOC'];
const FORMATS = ['PDF', 'EXCEL', 'CSV', 'HTML', 'JSON'];
const STATUSES = ['DRAFT', 'ACTIVE', 'SUSPENDED', 'ARCHIVED'];

/**
 * M37 — Report Builder (§28 / §9 Reporting Engine). Register of report
 * definitions (T495): scheduled / on-demand / ad-hoc, bound to a validated
 * dataset (BR-03), with an output format and lifecycle status. Authors create
 * reports (REPORT_MANAGE); SoD keeps the run-side distinct — generate a run
 * (REPORT_GENERATE), attach a cron schedule (REPORT_MANAGE) or export to a file
 * (REPORT_EXPORT). Filterable by type and status. Tenant isolation is enforced
 * server-side (LAW 4). A 403 surfaces a clear permission message.
 */
export function ReportBuilderScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [reportType, setReportType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // Create form
  const [reportName, setReportName] = useState('');
  const [newType, setNewType] = useState('ON_DEMAND');
  const [category, setCategory] = useState('');
  const [datasetId, setDatasetId] = useState('');
  const [outputFormat, setOutputFormat] = useState('PDF');
  const [newStatus, setNewStatus] = useState('DRAFT');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Schedule panel
  const [scheduleReport, setScheduleReport] = useState<ReportRow | null>(null);
  const [scheduleCron, setScheduleCron] = useState('0 6 * * *');
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (reportType) params.set('reportType', reportType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: ReportRow[]; total: number }>(`/v1/bi/reports${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reportType, status, q, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!reportName.trim()) { setActionError(t('m37.report.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ReportRow }>(`/v1/bi/reports`, {
        method: 'POST',
        body: {
          reportName: reportName.trim(),
          reportType: newType,
          category: category.trim() || undefined,
          datasetId: datasetId.trim() || undefined,
          outputFormat,
          status: newStatus,
        },
        token,
      });
      setNotice(t('m37.report.created', { code: r.data.reportCode, name: r.data.reportName }));
      setReportName('');
      setCategory('');
      setDatasetId('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.report.notAuthCreate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const generate = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(id);
    try {
      const r = await apiRequest<{ data: { rowCount: number; outputFormat: string; generatedAt: string } }>(`/v1/bi/reports/${id}/generate`, { method: 'POST', body: {}, token });
      setNotice(t('m37.report.generated', { rowCount: r.data.rowCount, format: r.data.outputFormat, when: at(r.data.generatedAt) }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.report.notAuthGenerate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const doExport = async (row: ReportRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusyId(row.reportId);
    try {
      const r = await apiRequest<{ data: { outputFormat: string; rowCount: number; exportedAt: string } }>(`/v1/bi/reports/${row.reportId}/export`, {
        method: 'POST',
        body: { outputFormat: row.outputFormat },
        token,
      });
      setNotice(t('m37.report.exported', { rowCount: r.data.rowCount, format: r.data.outputFormat, when: at(r.data.exportedAt) }));
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.report.notAuthExport'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const submitSchedule = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setScheduleError(null);
    if (!scheduleReport) return;
    if (!scheduleCron.trim()) { setScheduleError(t('m37.report.cronRequired')); return; }
    setScheduleBusy(true);
    try {
      await apiRequest<{ data: ReportRow }>(`/v1/bi/reports/${scheduleReport.reportId}/schedule`, {
        method: 'POST',
        body: { scheduleCron: scheduleCron.trim() },
        token,
      });
      setNotice(t('m37.report.scheduled', { code: scheduleReport.reportCode, cron: scheduleCron.trim() }));
      setScheduleReport(null);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setScheduleError(t('m37.report.notAuthSchedule'));
      } else {
        setScheduleError((err as Error).message);
      }
    } finally {
      setScheduleBusy(false);
    }
  };

  return (
    <main aria-label="report-builder">
      <h1>{t('m37.report.title')}</h1>

      <section aria-label="create-report">
        <h2>{t('m37.report.createTitle')}</h2>
        <form aria-label="report-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m37.common.name')}<input aria-label="field-name" value={reportName} onChange={(e) => setReportName(e.target.value)} /></label>
          <label>{t('m37.common.type')}
            <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.category')}<input aria-label="field-category" value={category} onChange={(e) => setCategory(e.target.value)} /></label>
          <label>{t('m37.report.datasetId')}<input aria-label="field-dataset" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} placeholder={t('m37.report.datasetPlaceholder')} /></label>
          <label>{t('m37.report.outputFormat')}
            <select aria-label="field-format" value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)}>
              {FORMATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="field-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-report" disabled={busy}>{busy ? t('m37.common.saving') : t('m37.report.createBtn')}</Button>
        </form>
      </section>

      {actionError && <p role="alert" aria-label="action-error">{actionError}</p>}
      {notice && <p role="status" aria-label="notice">{notice}</p>}

      <section aria-label="report-list">
        <h2>{t('m37.report.listTitle')}</h2>
        <div className="filters">
          <label>{t('m37.common.type')}
            <select aria-label="filter-type" value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {['', ...TYPES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {['', ...STATUSES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.search')}<input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m37.common.searchPlaceholder')} /></label>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="report-count">{t('m37.report.count', { count: total })}</p>
            <DataTable
              ariaLabel="report-table"
              rows={rows}
              rowKey={(r) => r.reportId}
              columns={[
                { key: 'reportCode', header: t('m37.common.code'), render: (r) => r.reportCode },
                { key: 'reportName', header: t('m37.common.name'), render: (r) => r.reportName },
                { key: 'reportType', header: t('m37.common.type'), render: (r) => r.reportType },
                { key: 'outputFormat', header: t('m37.report.colFormat'), render: (r) => r.outputFormat },
                { key: 'lastRunStatus', header: t('m37.report.colLastRun'), render: (r) => r.lastRunStatus },
                { key: 'scheduleEnabled', header: t('m37.report.colScheduled'), render: (r) => (r.scheduleEnabled ? t('common.yes') : t('common.no')) },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
                { key: 'actions', header: t('m37.common.actions'), render: (r) => (
                      <>
                      <Button variant="primary" type="button" aria-label={`generate-${r.reportId}`} disabled={busyId === r.reportId} onClick={() => void generate(r.reportId)}>{t('m37.report.generate')}</Button>
                      <Button variant="secondary" type="button" aria-label={`export-${r.reportId}`} disabled={busyId === r.reportId} onClick={() => void doExport(r)}>{t('m37.report.export')}</Button>
                      <Button variant="secondary" type="button" aria-label={`schedule-${r.reportId}`} onClick={() => { setScheduleReport(r); setScheduleError(null); }}>{t('m37.report.schedule')}</Button>
                      </>
                ) },
              ]}
            />
          </>
        )}
      </section>

      {scheduleReport && (
        <section aria-label="schedule-report">
          <h2>{t('m37.report.scheduleTitle', { name: scheduleReport.reportName })}</h2>
          <form aria-label="schedule-form" onSubmit={(e) => void submitSchedule(e)}>
            <label>{t('m37.report.cron')}<input aria-label="schedule-cron" value={scheduleCron} onChange={(e) => setScheduleCron(e.target.value)} placeholder="0 6 * * *" /></label>
            <Button variant="primary" type="submit" aria-label="submit-schedule" disabled={scheduleBusy}>{scheduleBusy ? t('m37.report.scheduling') : t('m37.report.saveSchedule')}</Button>
            <Button variant="danger" type="button" aria-label="cancel-schedule" onClick={() => setScheduleReport(null)}>{t('m37.common.close')}</Button>
          </form>
          {scheduleError && <p role="alert" aria-label="schedule-error">{scheduleError}</p>}
        </section>
      )}
    </main>
  );
}
